import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { FileText, Clock, CheckCircle2, TrendingUp, Euro, DollarSign } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import ClientSidebar from '../Sidebar/ClientSidebar';

const ClientDashboardNew = ({ user }) => {
  const { toast } = useToast();
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    completedOrders: 0,
    totalSpent: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      // Load both orders and consultings
      const [ordersRes, consultingsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/consulting/`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      const ordersData = await ordersRes.json();
      const consultingsData = await consultingsRes.json();

      // Calculate stats
      const totalSpent = 
        ordersData.filter(o => o.payment_status === 'paid').reduce((sum, o) => sum + (o.price || 0), 0) +
        consultingsData.filter(c => c.payment_status === 'paid').reduce((sum, c) => sum + (c.price || 0), 0);

      setStats({
        totalOrders: ordersData.length,
        pendingOrders: ordersData.filter(o => o.status === 'in_progress' || o.status === 'pending').length,
        completedOrders: ordersData.filter(o => o.status === 'completed').length,
        totalConsultings: consultingsData.length,
        pendingConsultings: consultingsData.filter(c => c.status === 'in_progress' || c.status === 'pending' || c.status === 'pending_payment').length,
        completedConsultings: consultingsData.filter(c => c.status === 'completed').length,
        totalSpent: totalSpent
      });

    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger les statistiques",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111111]">
      <ClientSidebar />
      <div className="lg:ml-64 pt-24 pb-12 px-3 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">
              Bienvenue, <span className="text-[#C00000]">{user?.name || 'Client'}</span>
            </h1>
            <p className="text-[#A6A6A6]">Vue d'ensemble de votre activité</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#A6A6A6] text-sm mb-1">Tuning en cours</p>
                    <p className="text-3xl font-bold text-white">{stats.pendingOrders}</p>
                  </div>
                  <Clock className="h-10 w-10 text-yellow-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#A6A6A6] text-sm mb-1">Tuning terminés</p>
                    <p className="text-3xl font-bold text-white">{stats.completedOrders}</p>
                  </div>
                  <CheckCircle2 className="h-10 w-10 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20 border-l-4 border-l-blue-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#A6A6A6] text-sm mb-1">Consulting en cours</p>
                    <p className="text-3xl font-bold text-white">{stats.pendingConsultings}</p>
                  </div>
                  <Clock className="h-10 w-10 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20 border-l-4 border-l-blue-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#A6A6A6] text-sm mb-1">Consulting terminés</p>
                    <p className="text-3xl font-bold text-white">{stats.completedConsultings}</p>
                  </div>
                  <CheckCircle2 className="h-10 w-10 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20 border-l-4 border-l-[#C00000]">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#A6A6A6] text-sm mb-1">Dépenses Total</p>
                    <p className="text-3xl font-bold text-[#C00000]">{stats.totalSpent.toFixed(0)}€</p>
                  </div>
                  <DollarSign className="h-10 w-10 text-[#C00000]" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20 hover:border-[#C00000]/50 transition-colors cursor-pointer"
                  onClick={() => window.location.href = '/client/new-order'}>
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-3">
                  <FileText className="h-6 w-6 text-[#C00000]" />
                  Nouvelle demande tuning
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#A6A6A6]">Commandez un fichier de reprogrammation ECU optimisé</p>
              </CardContent>
            </Card>

            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20 hover:border-[#C00000]/50 transition-colors cursor-pointer"
                  onClick={() => window.location.href = '/client/consulting'}>
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-3">
                  <TrendingUp className="h-6 w-6 text-[#C00000]" />
                  Consulting diagnostic
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#A6A6A6]">Obtenez de l'aide pour diagnostiquer un problème véhicule</p>
              </CardContent>
            </Card>
          </div>

          {/* Info Section */}
          <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20 mt-6">
            <CardHeader>
              <CardTitle className="text-white">Bienvenue sur GT ENGINEERING</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-[#A6A6A6]">
                Plateforme professionnelle de reprogrammation ECU et consulting automobile.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#C00000] rounded-full mt-2"></div>
                  <div>
                    <p className="text-white font-semibold mb-1">Tuning Files</p>
                    <p className="text-[#A6A6A6] text-sm">Reprogrammation ECU Stage 1, 2, 3 et conversions</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#C00000] rounded-full mt-2"></div>
                  <div>
                    <p className="text-white font-semibold mb-1">Consulting</p>
                    <p className="text-[#A6A6A6] text-sm">Aide au diagnostic de problèmes techniques</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#C00000] rounded-full mt-2"></div>
                  <div>
                    <p className="text-white font-semibold mb-1">Support 24/7</p>
                    <p className="text-[#A6A6A6] text-sm">Chat direct avec nos ingénieurs</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboardNew;
